import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFormik } from 'formik'
import styled from 'styled-components'
import { theme } from 'styled-tools'

import {
  validateEmail,
  validateName,
  validatePhoneNumber,
} from '../../../utils/validation'
import { useTranslation } from '../i18n/useTranslation'
import {
  createAppointment,
  geocodeAddress,
  useGetStores,
} from '../../../utils/badaAppointmentApi'
import { isDesigner, user } from '../../../utils/initValues'
import features from '../features/'
import { FormSliderQuestion } from '../quick-questions/questions/FormSliderQuestion'
import BaseButton from '../button/baseButton'
import { AddressInput } from './AddressInput'
import { Checkbox, Radio } from './RadioCheckbox'
import { Input, Select, SelectWrapper, Textarea } from './Input'
import CheckmarkFilled from '../icons/checkmarkFilled'
import InformationInputIcon from '../icons/informationInput'
import { calcDistance } from '../../../utils/distance'
import { Verification } from '../customer-form/Verification'
import { GoogleReCaptcha, RecaptchaMessage } from '../recaptcha'
import Loader from '../loader/Loader'
import tracker, { trackFormError } from '../../../utils/tracker'
import { useCmsContent } from '../cms/useCmsContent'
import { Overlay } from '../overlay'
import TermsOverlay from '../customer-form/TermsOverlay'
import { clearState } from '../../../utils/state'
import settings from '../settings'
import appInsights, { SeverityLevel } from '../logging/appInsights'
import transformStore from '../../containers/bada/reducer/transform'
import { appBaseUrl } from '../../../utils/appBaseUrl'

const initialValues = (questions) => ({
  name: '',
  email: '',
  phone: '',
  newsletterConsent: false,
  comments: '',

  address: '',
  address2: '',
  address3: '',
  city: '',
  zipcode: '',
  country: '',

  questions: questions.map((q) => ({
    id: q.id,
    question: q.questionText,
    answers: [],
  })),

  storeId: '',
  preselectedStoreId: false,

  recaptchaToken: '',
  verificationToken: {
    token: '',
    id: '',
    verified: false,
    required: false,
  },
})

/**
 * @param {{
 *  service: {
 *    id: string
 *    code: string
 *    visibility: string
 *    questions: any[]
 *  }
 *  storeId?: string
 *  onCreated: () => void
 *  updateUiState: (partialState: any) => void
 * }} props
 */
export const RequestForm = ({ service, storeId, onCreated, updateUiState }) => {
  const { t } = useTranslation('app.form')
  const { data: stores } = useGetStores(window.location.search.includes('fake'))
  const { pageContent } = useCmsContent()
  const [selectStore, setSelectStore] = useState(false)
  const [showVerification, setShowVerification] = useState(false)
  const [externalSubmitting, setExternalSubmitting] = useState(false)
  const [geocoding, setGeocoding] = useState(false)
  const [overlayOpen, setOverlayOpen] = useState(false)
  const prevSubmitCount = useRef(0)
  const [delayedSubmitCount, setDelayedSubmitCount] = useState(0)
  const [singleApponintment, setSingleAppointment] = useState('')
  const [error, setError] = useState('')

  const [location, setLocation] = useState({
    latitude: undefined,
    longitude: undefined,
  })

  const storeOptions = useMemo(
    () =>
      [...stores]
        .filter(Boolean)
        .map((s) => ({
          ...s,
          ...calcDistance(
            location.latitude,
            location.longitude,
            s.latitude,
            s.longitude
          ),
        }))
        .sort((a, b) => a.storeName.localeCompare(b.storeName)),
    [stores, location.latitude, location.longitude]
  )

  const {
    values,
    errors,
    touched,
    setFieldValue,
    setValues,
    submitForm,
    submitCount,
    isSubmitting,
    handleBlur,
    handleChange,
    handleSubmit,
    validateForm,
  } = useFormik({
    initialValues: initialValues(service.questions),
    validate: async (vals) => {
      const errs = {}
      const phoneValidation = vals.phone
        ? validatePhoneNumber(vals.phone)
        : Promise.resolve('required')
      const emailValidation = vals.email
        ? validateEmail(vals.email)
        : Promise.resolve('required')

      const name = validateName(vals.name || '')
      if (!name) {
        errs.name = 'required'
      }
      const phone = await phoneValidation
      if (phone) {
        errs.phone = phone
      }

      const email = await emailValidation
      if (email) {
        errs.email = email
      }
      if (!vals.address) {
        errs.address = 'required'
      }
      if (!vals.storeId) {
        errs.storeId = 'required'
      }
      if (!vals.comments && service.requiredTextarea) {
        errs.comments = 'required'
      }

      const questions = service?.questions?.filter((q) => q.required) || []
      const unansweredQuestions = questions
        .map((q) => ({
          id: q.id,
          answered:
            vals.questions?.find(
              (qq) => q.id === qq.id || q.questionText === qq.question
            )?.answers?.length > 0 || false,
        }))
        .filter((q) => !q.answered)

      if (unansweredQuestions.length > 0) {
        errs.questions = unansweredQuestions.map((q) => q.id)
      }

      if (
        !Object.keys(errs).length &&
        !vals.verificationToken.token &&
        features.verification
      ) {
        errs.verificationToken = 'verification-required'
      }

      if (
        !Object.keys(errs).length &&
        !vals.recaptchaToken &&
        features.useRecaptcha
      ) {
        errs.recaptchaToken = 'recaptcha-required'
      }

      return errs
    },
    onSubmit: async (vals, { setSubmitting }) => {
      const trackingCookieMatch = `; ${document.cookie}`.match(
        '; _mkto_trk=([^;]+)'
      )
      const trackingCookie = trackingCookieMatch ? trackingCookieMatch[1] : null

      const affiliateCookieMatch = `; ${document.cookie}`.match(
        '; cust-utm-term-v2=([^;]+)'
      )
      const affiliateTag = affiliateCookieMatch ? affiliateCookieMatch[1] : null

      const sourceCookieMatch = `; ${document.cookie}`.match(
        '; cust-utm-source=([^;]+)'
      )
      const utmSource = sourceCookieMatch ? sourceCookieMatch[1] : null

      const transactionIdCookieMatch = `; ${document.cookie}`.match(
        '; cust-transaction-id=([^;]+)'
      )
      const transactionId = transactionIdCookieMatch
        ? transactionIdCookieMatch[1]
        : null

      const utmMediumCookieMatch = `; ${document.cookie}`.match(
        '; cust-utm-medium=([^;]+)'
      )
      const utmMedium = utmMediumCookieMatch ? utmMediumCookieMatch[1] : null

      const input = {
        serviceId: service.id,
        service: service.code,
        bookingSystem: service.visibility,

        storeId: vals.storeId,

        email: vals.email.trim().toLowerCase(),
        name: vals.name.trim(),
        address: vals.address,
        address2: vals.address2,
        address3: vals.address3,
        phone: vals.phone,
        postCode: vals.zipcode,
        city: vals.city,
        country: vals.country,
        comments: vals.comments.trim(),
        newsletterConsent: vals.newsletterConsent,

        userIdentifier: user.email,
        bookedByDesigner: isDesigner,

        trackingCookie,
        flow: null,

        affiliateTag,
        transactionId,
        utmSource,
        utmMedium,

        recaptchaToken: vals.recaptchaToken,
        verificationToken: vals.verificationToken.token,
        questions: vals.questions || [],
      }
      updateUiState({
        values: Object.fromEntries(
          Object.entries(vals).map(([k, v]) => [
            k,
            typeof v === 'string' ? v.trim() : v,
          ])
        ),
        customerFormInput: input,
      })

      setSubmitting(true)
      try {
        const { id } = await createAppointment({
          input,
        })
        updateUiState({
          booking: { loading: false },
          appointmentId: id,
          bookingCompleted: true,
        })
        clearState(settings.brand)
        onCreated()
      } catch (err) {
        const [first, appointmentId] = err.message?.split(' ') || []
        if (first === 'single-appointment' && !!appointmentId) {
          setSingleAppointment(appointmentId)
          setError('')
        } else if (first === 'recaptcha-low') {
          setSingleAppointment('')
          setError(first)
        } else {
          setSingleAppointment('')
          setError('Unfortunately we could not complete your request')
          appInsights.trackException({
            exception: err,
            severityLevel: SeverityLevel.Error,
          })
        }
        updateUiState({
          booking: { error: { err } },
          bookingCompleted: true,
        })
      } finally {
        setSubmitting(false)
      }
    },
  })

  const selectedStore = useMemo(
    () => storeOptions.find((s) => s.id === values.storeId),
    [storeOptions, values.storeId]
  )

  useEffect(() => {
    setTimeout(() => {
      setDelayedSubmitCount(submitCount)
    }, 50)
  }, [submitCount])

  useEffect(() => {
    const allowed = storeOptions.some((s) => s.id === storeId)
    if (storeId && allowed && storeOptions.length) {
      setFieldValue('storeId', storeId)
      setFieldValue('preselectedStoreId', true)
    }
  }, [storeId, storeOptions])

  useEffect(() => {
    updateUiState({
      selectedAddress: values.address
        ? { city: values.city, postalCode: values.zipcode }
        : null,
    })
  }, [values.address, values.city, values.zipcode, updateUiState])

  useEffect(() => {
    updateUiState({ prepData: { questions: values.questions } })
  }, [values.questions, updateUiState])

  useEffect(() => {
    updateUiState({
      selectedStore: selectedStore
        ? { ...transformStore({ store: selectedStore }) }
        : null,
    })
  }, [selectedStore, updateUiState])

  const onChangeQuestion = useCallback(
    (question, answer) => {
      const idx = values.questions.findIndex((q) => q.id === question.id)
      if (idx < 0) {
        return
      }

      const single = question.optionType === 'single'
      const multi = question.optionType === 'multi'
      const slider = question.optionType === 'slider'
      const updatedQuestion = { ...values.questions[idx] }
      let updatedAnswer = [...(updatedQuestion.answers || [])]
      if (single || multi) {
        const hasAnswer = updatedQuestion.answers.includes(answer)
        if (single) {
          updatedAnswer = hasAnswer ? [] : [answer]
        } else if (multi) {
          updatedAnswer = hasAnswer
            ? updatedAnswer.filter((a) => a !== answer)
            : [...updatedAnswer, answer]
        }
      } else if (slider) {
        updatedAnswer = [answer]
      }

      updatedQuestion.answers = updatedAnswer
      setFieldValue('questions', [
        ...values.questions.slice(0, idx),
        updatedQuestion,
        ...values.questions.slice(idx + 1),
      ])

      tracker.trackQuestionFilled(
        updatedQuestion.id,
        updatedQuestion.answers.join(', '),
        {}
      )
    },
    [setFieldValue, values.questions]
  )

  const trackSubmit = useCallback(() => {
    Object.entries(errors).forEach(([key, value]) => {
      if (key === 'questions') {
        if (!Array.isArray(value)) {
          return
        }
        value.forEach((v) => {
          trackFormError(`questions_${v}`, 'required', '')
        })
      } else {
        trackFormError(key, value, values[key])
      }
    })

    const success = Object.keys(errors).length === 0
    const failed =
      Object.keys(errors).filter(
        (k) => !['verificationToken', 'recaptchaToken'].includes(k)
      ).length > 0
    const eventCategory = 'BADA 2.0 submit form'
    if (success) {
      const { id, required, verified } = values.verificationToken
      let requireOTP = ''
      if (verified) {
        requireOTP = 'require'
      } else if (required) {
        requireOTP = 'control'
      } else {
        requireOTP = 'not-require'
      }
      const extra = id ? { verificationId: id, requireOTP } : {}
      tracker.trackClickSuccess(eventCategory, selectedStore?.storeName, extra)
      tracker.trackClick(
        'BADA 2.0 newsletter',
        values.newsletterConsent ? 'true' : 'false',
        {}
      )
    } else if (failed) {
      tracker.trackClickFail(eventCategory, selectedStore?.storeName)
    }
  }, [selectedStore?.storeName, errors, values])

  useEffect(() => {
    // Clicked submit button or triggered submitForm()
    if (prevSubmitCount.current < delayedSubmitCount) {
      prevSubmitCount.current += 1
      trackSubmit()
      if (errors.verificationToken) {
        setShowVerification(true)
      }
    }
    // We triggered submit by increasing prevSubmitCount
    else if (prevSubmitCount.current > delayedSubmitCount) {
      prevSubmitCount.current = delayedSubmitCount
      submitForm()
    }
  }, [prevSubmitCount, delayedSubmitCount, trackSubmit, validateForm, errors])

  const onBlur = (event) => {
    handleBlur(event)
    const name = event.target.name
    if (!name) {
      return
    }

    updateUiState({
      customerForm: { touched: true },
    })
    tracker.trackFilled('BADA 2.0 dataform', name, {})
    validateForm().then((errs) => {
      const err = errs[name]
      if (err) {
        trackFormError(name, err, values[name])
      }
    })
  }

  const onVerificationConfirmed = useCallback(
    (token, id, verified, required) => {
      setFieldValue('verificationToken', { token, id, verified, required })
      prevSubmitCount.current += 1
    },
    [setFieldValue]
  )

  const onRecaptchaVerified = useCallback(
    (token) => {
      setFieldValue('recaptchaToken', token)
      prevSubmitCount.current += 1
    },
    [setFieldValue]
  )

  useEffect(() => {
    const submitting =
      !!errors.recaptchaToken ||
      (!!errors.verificationToken && showVerification)
    const time = submitting ? 0 : 100
    setTimeout(() => {
      setExternalSubmitting(submitting)
    }, time)
  }, [errors.recaptchaToken, errors.verificationToken, showVerification])

  useEffect(() => {
    const lines = [values.address, values.address2, values.address3]
      .filter(Boolean)
      .join(' ')
    const address = [lines, values.zipcode, values.city]
      .filter(Boolean)
      .join(', ')
    if (address) {
      setGeocoding(true)
      geocodeAddress(address)
        .then((r) => {
          if (r.latitude && r.longitude) {
            setLocation({
              latitude: r.latitude,
              longitude: r.longitude,
            })
          }
        })
        .finally(() => {
          setGeocoding(false)
        })
    }
  }, [
    values.address,
    values.address2,
    values.address3,
    values.zipcode,
    values.city,
  ])

  useEffect(() => {
    if (values.preselectedStoreId) {
      return () => {}
    }

    if (!values.address) {
      return () => {}
    }

    if (geocoding) {
      return () => {}
    }

    const options = [...storeOptions].sort((a, b) =>
      a.distance && b.distance ? a.distance - b.distance : 0
    )
    const timeout = setTimeout(() => {
      const closest = options.find((o) => !!o.distance)
      if (closest) {
        setFieldValue('storeId', closest.id)
      } else {
        setSelectStore(true)
      }
    }, 200)

    return () => {
      clearTimeout(timeout)
    }
  }, [values.address, values.preselectedStoreId, storeOptions, geocoding])

  useEffect(() => {
    if (selectedStore) {
      return
    }
    if (submitCount === 0) {
      return
    }
    if (errors.storeId) {
      setSelectStore(true)
    }
  }, [submitCount, errors.storeId, selectedStore])

  const hasFieldError = (field) => !!errors[field]

  const hasQuestionError = (id) => !!errors.questions?.includes(id)

  const renderValidIcon = (field) =>
    touched[field] &&
    !!values[field] &&
    !hasFieldError(field) && (
      <IconWrapper $valid>
        <CheckmarkFilled width={23} height={23} />
      </IconWrapper>
    )

  const renderErrorIcon = (field) =>
    (touched[field] || submitCount > 0) &&
    (hasFieldError(field) || hasQuestionError(field?.id)) && (
      <IconWrapper $error>
        <InformationInputIcon width={23} height={23} />
      </IconWrapper>
    )

  const renderError = (field) =>
    submitCount > 0 &&
    ((hasFieldError(field) && <Error>{t(`${field}.error`)}</Error>) ||
      (hasQuestionError(field?.id) && (
        <Error>{field?.requiredText || t('required')}</Error>
      )))

  const renderField = (label, field, children, renderIcons = true) => (
    <Label>
      <InputTitle>{label}</InputTitle>
      <InputWrapper>
        {children}
        {renderIcons && renderValidIcon(field)}
        {renderIcons && renderErrorIcon(field)}
      </InputWrapper>
      {renderError(field)}
    </Label>
  )

  return (
    <Form onSubmit={handleSubmit}>
      {renderField(
        service.formName,
        'name',
        <Input
          name="name"
          value={values.name}
          onChange={handleChange}
          onBlur={onBlur}
        />
      )}
      {renderField(
        service.formEmail,
        'email',
        <Input
          name="email"
          value={values.email}
          onChange={handleChange}
          onBlur={onBlur}
        />
      )}
      {renderField(
        service.formPhone,
        'phone',
        <Input
          name="phone"
          value={values.phone}
          onChange={handleChange}
          onBlur={onBlur}
        />
      )}
      {renderField(
        service.formAddress,
        'address',
        <AddressInput
          selectedAddress={{
            address: values.address,
            address2: values.address2,
            address3: values.address3,
            postalCode: values.zipcode,
            city: values.city,
            country: values.country,
          }}
          onBlur={onBlur}
          onClearAddress={() => {
            setValues({
              ...values,
              address: '',
              address2: '',
              address3: '',
              zipcode: '',
              city: '',
              country: '',
            })
          }}
          onSelectAddress={({
            address,
            address2,
            address3,
            postalCode,
            city,
            country,
          }) => {
            setValues({
              ...values,
              address,
              address2,
              address3,
              zipcode: postalCode,
              city,
              country,
            })
          }}
        />
      )}
      {selectStore &&
        renderField(
          'Store',
          'storeId',
          <SelectWrapper>
            <Select
              name="storeId"
              value={values.storeId}
              onChange={handleChange}
              onBlur={onBlur}
            >
              <option value="" disabled hidden />
              {storeOptions.map((o) => (
                <option value={o.id} key={o.id}>
                  {o.storeName}
                </option>
              ))}
            </Select>
          </SelectWrapper>
        )}
      {!selectStore && !!selectedStore && (
        <SelectedStore>
          <span>
            Your local store is <strong>{selectedStore.storeName}</strong>
          </span>

          <ChangeStore type="button" onClick={() => setSelectStore(true)}>
            Change store
          </ChangeStore>
        </SelectedStore>
      )}
      {!!service.showTextareaField &&
        renderField(
          service.formTextarea,
          'comments',
          <Textarea
            name="comments"
            value={values.comments}
            rows={5}
            onChange={handleChange}
            onBlur={onBlur}
          />
        )}
      {service.questions.map((q) => (
        <Question key={q.id || q.questionText}>
          <QuestionTitle>
            <InputTitle>
              {q.questionTextOnBadaForm || q.questionText}

              {renderErrorIcon(q)}
            </InputTitle>
            {!!q.otherAnswerText && (
              <Checkbox
                id={`other-checkbox-${q.id}`}
                label={q.otherAnswerText}
                value={q.otherAnswerText}
                onChange={() => onChangeQuestion(q, q.otherAnswerText)}
              />
            )}
          </QuestionTitle>
          {!!q.questionDescription && (
            <InputHelpText>{q.questionDescription}</InputHelpText>
          )}
          <QuestionOptions>
            {q.options.map((o) => (
              <QuestionOption key={o.name}>
                {q.optionType === 'single' && (
                  <Radio
                    id={`radio-${o.id}-${o.name.replace(/[^\w]/g, '')}`}
                    value={o.name}
                    label={o.name}
                    onChange={() => onChangeQuestion(q, o.name)}
                    checked={
                      !!values?.questions
                        ?.find((qq) => qq.id === q.id)
                        ?.answers?.includes(o.name || '')
                    }
                  />
                )}
                {q.optionType === 'multi' && (
                  <Checkbox
                    id={`checkbox-${o.id}-${o.name.replace(/[^\w]/g, '')}`}
                    label={o.name || ''}
                    value={o.name || ''}
                    onChange={() => onChangeQuestion(q, o.name)}
                    checked={
                      !!values?.questions
                        ?.find((qq) => qq.id === q.id)
                        ?.answers?.includes(o.name || '')
                    }
                  />
                )}
                {q.optionType === 'slider' && (
                  <FormSliderQuestion
                    question={q}
                    answers={
                      values.questions.find((qq) => qq.id === q.id)?.answers ||
                      []
                    }
                    onChange={([answer]) => onChangeQuestion(q, answer)}
                  />
                )}
              </QuestionOption>
            ))}
            {renderError(q)}
          </QuestionOptions>
        </Question>
      ))}

      <InputWrapper>
        <Checkbox
          id="request-consent"
          name="newsletterConsent"
          label={service.formOptin}
          checked={values.newsletterConsent}
          onChange={handleChange}
          onBlur={onBlur}
        />
      </InputWrapper>

      {!!singleApponintment && (
        <Information>
          <InformationHeading>You already have a booking</InformationHeading>
          <InformationText>
            You already have a scheduled appointment. if you need to make
            changes, you can{' '}
            <InformationLink
              href={`${appBaseUrl}/amend/${singleApponintment}`}
              target="_blank"
            >
              reschedule
            </InformationLink>{' '}
            or{' '}
            <InformationLink
              href={`${appBaseUrl}/cancel/${singleApponintment}`}
              target="_blank"
            >
              cancel
            </InformationLink>{' '}
            your booking at any time.
          </InformationText>
        </Information>
      )}
      {!!error && (
        <Information>
          <InformationHeading>
            Sorry we can&apos;t complete your request right now
          </InformationHeading>
          <InformationText>
            Please call your selected store {selectedStore.name} to complete
            your request
          </InformationText>
          <InformationTextStrong>
            <InformationExternalLink href={`tel:${selectedStore.phoneNumber}`}>
              {selectedStore.phoneNumber}
            </InformationExternalLink>
          </InformationTextStrong>
        </Information>
      )}
      {(isSubmitting || externalSubmitting) && <Loader duration={3} />}
      <Button block type="submit" disabled={isSubmitting || externalSubmitting}>
        {service.formSubmitButtonText}
      </Button>
      <div>
        {features.useRecaptcha && <RecaptchaMessage />}
        {pageContent.showTermsAndConditions && (
          <>
            <TermsConditions>
              {pageContent.termsAndConditionsText}
            </TermsConditions>
            &nbsp;
            <TermsConditionsLink onClick={() => setOverlayOpen(true)}>
              {pageContent.termsAndConditionsLinkLabel}
            </TermsConditionsLink>
            <Overlay
              open={overlayOpen}
              handleChange={() => setOverlayOpen(false)}
              setClosed={() => setOverlayOpen(false)}
              render={({ setClosed }) => (
                <TermsOverlay
                  setClosed={setClosed}
                  termsAndConditionsSummary={
                    pageContent.termsAndConditionsSummary
                  }
                />
              )}
            />
          </>
        )}
      </div>
      {hasFieldError('verificationToken') && (
        <Verification
          show={showVerification}
          email={values.email}
          phoneNumber={values.phone}
          storeId={values.storeId}
          storeName={selectedStore?.storeName}
          meetingType={service.code}
          onClose={() => setShowVerification(false)}
          onConfirmed={onVerificationConfirmed}
        />
      )}
      {hasFieldError('recaptchaToken') && (
        <GoogleReCaptcha action="submit" onVerify={onRecaptchaVerified} />
      )}
    </Form>
  )
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 8px;

  margin: 16px;
`

const IconWrapper = styled.span`
  color: ${(p) => p.$error && p.theme.forms.colors.invalid};
  color: ${(p) => p.$valid && p.theme.forms.colors.valid};
`

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 5px;
`

const InputWrapper = styled.div`
  position: relative;

  ${IconWrapper} {
    position: absolute;
    top: 13px;
    right: 13px;

    z-index: 20;
  }
`

const InputTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  font-weight: 500;
`
const InputHelpText = styled.div`
  font-size: 13px;
`

const Question = styled.div``

const QuestionTitle = styled.div`
  display: flex;
  justify-content: space-between;
`

const QuestionOptions = styled.div`
  margin-top: 8px;
`

const QuestionOption = styled.div`
  margin-bottom: 6px;
`

const Error = styled.span`
  color: ${(p) => p.theme.forms.colors.invalid};
  font-size: ${(p) => p.theme.forms?.errorMessage?.fontSize};

  padding-top: 7px;
`

const Button = styled(BaseButton)`
  margin-top: 10px;
`

const SelectedStore = styled.div`
  background: ${(p) => p.theme.colors.secondaryBackgroundColor};
  padding: 13px 13px;

  display: flex;
  justify-content: space-between;
`

const ChangeStore = styled.button`
  text-decoration: underline;
  background: transparent;
  border: 0;
  cursor: pointer;
`

const TermsConditions = styled.span`
  font-family: ${theme('typography.paragraph.fontFamily')};
  font-size: 14px;
  font-weight: 300;
  color: ${theme('forms.termsConditions.color')};
`

const TermsConditionsLink = styled.span`
  font-family: ${theme('typography.paragraph.fontFamily')};
  font-size: ${theme('forms.termsConditions.fontSize')};
  font-weight: ${theme('forms.termsConditions.fontWeight')};
  color: ${theme('forms.termsConditions.color')};
  border-bottom: ${theme('forms.label.link.borderBottom')};
  border-bottom-color: ${theme('forms.label.link.borderBottomColor')};
  padding-bottom: ${theme('forms.label.link.paddingBottom')};
  cursor: pointer;
`

const Information = styled.div`
  margin-top: 16px;
  background-color: ${theme('colors.toneA')};
  padding: 16px;

  display: flex;
  flex-direction: column;
  gap: 12px;
`
const InformationHeading = styled.div`
  font-weight: 700;
`
const InformationText = styled.div``
const InformationTextStrong = styled.div`
  font-weight: 500;
`
const InformationLink = styled.a`
  color: inherit;
`
const InformationExternalLink = styled.a`
  color: inherit;
`
